import { createRouter, createWebHistory } from "vue-router";
import { routeState } from "@/utils/routeState.js";
import {
  loginGuards,
  studentGuards,
  adminGuards,
  evaluatorGuards,
  proctorGuards,
} from "./guards";

const routes = [
  {
    path: "/:catchAll(.*)",
    name: "Error404",
    component: () => import("@/views/CommonPages/PageNotFound.vue"),
    meta: { title: "Page Not Found | ExamStack" },
  },
  {
    path: "/",
    name: "Login",
    component: () => import("../views/Authentication/LoginView.vue"),
    beforeEnter: loginGuards,
    meta: { title: "Login | ExamStack" },
  },
  // Admin dashboard
  {
    path: "/admin/dashboard",
    name: "AdminDashboardView",
    component: () => import("../views/AdminDashboard/DashboardView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin-exam",
    name: "ExamView",
    component: () => import("../views/AdminDashboard/ExamView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin-question-bank",
    name: "QuestionView",
    component: () =>
      import("../views/AdminDashboard/QuestionBank/QuestionView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/exam-builder/:id/:questionId?",
    name: "ExamBuilder",
    component: () =>
      import("@/views/AdminDashboard/QuestionBank/ExamBuilder.vue"),
    beforeEnter: adminGuards,
    props: true,
  },
  {
    path: "/question-create/:id?",
    name: "GenerateQuestions",
    component: () =>
      import("@/views/AdminDashboard/QuestionBank/GenerateQuestions.vue"),
    beforeEnter: adminGuards,
    props: true,
  },
  {
    path: "/student-list",
    name: "StudentList",
    component: () =>
      import("../views/AdminDashboard/StudentDetails/StudentList.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/user-list",
    name: "UserList",
    component: () => import("../views/AdminDashboard/UserList.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/question-review/:id?",
    name: "QuestionReview",
    component: () => import("../views/AdminDashboard/QuestionReview.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // exam configurations
  {
    path: "/exam-create/:id?",
    name: "ExamCreate",
    component: () => import("../views/AdminDashboard/Exam/ExamCreate.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/exam-configurations/:id?",
    name: "ExamConfiguration",
    component: () =>
      import("../views/AdminDashboard/Exam/ExamConfiguration.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin/exam/preview/:id?",
    name: "ExamDetails",
    component: () => import("../views/AdminDashboard/Exam/ExamDetails.vue"),
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin/live-kit",
    name: "LiveKitDashboard",
    component: () => import("../views/AdminDashboard/LiveKitDashboard.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/admin/live-kit-home",
    name: "LiveKitDashboardHome",
    component: () => import("../views/AdminDashboard/ExamListLiveKit.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },

  // admin reports
  {
    path: "/reports",
    name: "ExamReportsView",
    component: () => import("../views/AdminDashboard/ExamReportsView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/reports/exam",
    name: "AdminExamReports",
    component: () => import("../views/AdminDashboard/AdminExamReports.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/reports/question-bank-analysis",
    name: "ReportsQuestionBank",
    component: () =>
      import("../views/AdminDashboard/Reports/QuestionBankAnalysis.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/reports/question-bank",
    name: "QuestionBankReport",
    component: () =>
      import("../views/AdminDashboard/Reports/QuestionBankReport.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/reports/student-logs",
    name: "StudentLogs",
    component: () => import("../views/AdminDashboard/Reports/StudentLogs.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },

  // admin results
  {
    path: "/results/result-list",
    name: "AdminResults",
    component: () => import("../views/AdminDashboard/Result/AdminResults.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/results/result-approve",
    name: "ResultApproval",
    component: () =>
      import("../views/AdminDashboard/Result/ResultApproval.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // admin student details page
  {
    path: "/admin/student-detail/:id?",
    name: "StudentDetails",
    component: () =>
      import("..//views/AdminDashboard/StudentDetails/StudentDetails.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // email template
  {
    path: "/email/template-list",
    name: "EmailList",
    component: () =>
      import("../views/AdminDashboard/EmailTemplate/EmailList.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/email/template-form/:id?",
    name: "CreateEmailTemplate",
    component: () =>
      import("../views/AdminDashboard/EmailTemplate/CreateEmailTemplate.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/email/template-create/:id?",
    name: "EmailTemplate",
    component: () =>
      import("../views/AdminDashboard/EmailTemplate/EmailTemplate.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // settings
  {
    path: "/settings/",
    name: "SettingsView",
    component: () =>
      import("../views/AdminDashboard/Settings/SettingsView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  {
    path: "/settings/slots/",
    name: "SlotsView",
    component: () => import("../views/AdminDashboard/Settings/SlotsView.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // Evaluator routes
  {
    path: "/evaluator/dashboard/",
    name: "EvaluatorList",
    component: () =>
      import("../views/AdminDashboard/Evaluator/EvaluatorList.vue"),
    beforeEnter: adminGuards,
    meta: { title: "Admin | ExamStack" },
  },
  // Proctor routes
  {
    path: "/proctor/dashboard",
    name: "ProctorDashboard",
    component: () => import("../views/ProctorDashboard/DashboardView.vue"),
    beforeEnter: proctorGuards,
    meta: { title: "Proctor | ExamStack" },
  },
  // Student Routes
  {
    path: "/examine",
    name: "StudentDashboardView",
    component: () => import("@/views/StudentViews/DashboardView.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Student Dashboard | ExamStack" },
  },
  {
    path: "/examine/exam-acknowledgement",
    name: "StudentExamEnvironment",
    component: () => import("@/views/StudentViews/ExamAcknowledgement.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam Acknowledgement | ExamStack" },
  },
  {
    path: "/examine/system-check",
    name: "StudentSystemCheck",
    component: () => import("@/views/StudentViews/SystemCheck.vue"),
    beforeEnter: studentGuards,
    meta: { title: "System Check | ExamStack" },
  },
  {
    path: "/exam",
    name: "StudentExam",
    component: () => import("@/views/StudentViews/ExamLoader.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam | ExamStack" },
  },
  {
    path: "/exam-interface",
    name: "StudentExamInterface",
    component: () => import("@/views/StudentViews/ExamView.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam Interface | ExamStack" },
  },
  {
    path: "/exam-feedback",
    name: "StudentExamFeedback",
    component: () => import("@/views/StudentViews/FeedbackForm.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Feedback | ExamStack" },
  },
  {
    path: "/examine/exam-list",
    name: "StudentExamList",
    component: () => import("@/views/StudentViews/ExamList/ExamList.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Exam List | ExamStack" },
  },
  {
    path: "/examine/exam-result",
    name: "StudentExamResults",
    component: () => import("@/views/StudentViews/ExamList/ExamResult.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Result | ExamStack" },
  },
  {
    path: "/examine/exam-certificates",
    name: "StudentExamCertificates",
    component: () =>
      import("@/views/StudentViews/ExamList/ExamCertificates.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Certificates | ExamStack" },
  },
  {
    path: "/examine/profile",
    name: "StudentProfile",
    component: () => import("@/views/StudentViews/StudentProfile.vue"),
    beforeEnter: studentGuards,
    meta: { title: "Profile | ExamStack" },
  },
  // common pages
  {
    path: "/profile",
    name: "ProfileView",
    component: () => import("../views/CommonPages/ProfileView.vue"),
    meta: { title: "Profile | ExamStack" },
  },
  {
    path: "/forget-password",
    name: "ForgetPassword",
    component: () => import("../views/CommonPages/ForgetPassword.vue"),
    meta: { title: "FP | ExamStack" },
  },
  {
    path: "/rest-password/:id?",
    name: "ResetPassword",
    component: () => import("../views/CommonPages/ResetPassword.vue"),
    meta: { title: "FP | ExamStack" },
  },
  {
    path: "/notifications",
    name: "NotificationView",
    component: () => import("../views/CommonPages/NotificationView.vue"),
    meta: { title: "Notifications | ExamStack" },
  },
  {
    path: "/configuration/batch",
    name: "BatchList",
    component: () => import("@/views/CommonPages/Configurations/BatchList.vue"),
    meta: { title: "configuration | ExamStack" },
  },
  {
    path: "/configuration/program",
    name: "ProgramList",
    component: () =>
      import("@/views/CommonPages/Configurations/ProgramList.vue"),
    meta: { title: "configuration | ExamStack" },
  },
  {
    path: "/configuration/elective",
    name: "ElectiveList",
    component: () =>
      import("@/views/CommonPages/Configurations/ElectiveList.vue"),
    meta: { title: "configuration | ExamStack" },
  },
  {
    path: "/configuration/semester",
    name: "SemesterList",
    component: () =>
      import("@/views/CommonPages/Configurations/SemesterList.vue"),
    meta: { title: "configuration | ExamStack" },
  },
  {
    path: "/configuration/course",
    name: "CourseList",
    component: () =>
      import("@/views/CommonPages/Configurations/CourseList.vue"),
    meta: { title: "configuration | ExamStack" },
  },
  // Evaluator dashboard
  {
    path: "/evaluator/dashboard",
    name: "EvaluatorDashboard",
    component: () => import("../views/EvaluatorViews/EvaluatorDashboard.vue"),
    beforeEnter: evaluatorGuards,
    meta: { title: "Evaluator | ExamStack" },
  },
  {
    path: "/evaluator/exam",
    name: "EvaluatorExam",
    component: () => import("../views/EvaluatorViews/EvaluatorExam.vue"),
    beforeEnter: evaluatorGuards,
    meta: { title: "Evaluator | ExamStack" },
  },
  {
    path: "/evaluator/assigned-students/:id?",
    name: "AssignedStudents",
    component: () => import("../views/EvaluatorViews/AssignedStudents.vue"),
    beforeEnter: evaluatorGuards,
    meta: { title: "Evaluator | ExamStack" },
  },
  // service pages
  {
    path: "/maintenance",
    name: "MaintenanceView",
    component: () => import("../views/CommonPages/MaintenanceView.vue"),
    meta: { title: "Maintenance | ExamStack" },
  },
  // network error page
  {
    path: "/network-disconnect",
    name: "NetworkDisconnect",
    component: () => import("../views/CommonPages/NetworkDisconnect.vue"),
    meta: { title: "Network | ExamStack" },
  },
  // access denied page
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: () => import("../views/CommonPages/AccessDenied.vue"),
    meta: { title: "Network | ExamStack" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return { top: 0, behavior: "smooth" };
  },
});

router.afterEach((to) => {
  const defaultTitle = "ExamStack";

  if (to.meta && to.meta.title) {
    document.title = to.meta.title;
  } else {
    document.title = defaultTitle;
  }
});

router.beforeEach((to, from, next) => {
  const isOffline = !navigator.onLine;

  if (!isOffline && to.name !== "NetworkDisconnect") {
    routeState.lastRoute = to.fullPath;
  }

  next();
});

// router.beforeEach((to, from, next) => {
//   const isOffline = !window.navigator.onLine;

//   if (!isOffline && to.name !== "NetworkDisconnect") {
//     localStorage.setItem("last_visited_page", to.fullPath);
//   }

//   if (isOffline) {
//     if (to.name !== "NetworkDisconnect") {
//       return next({ name: "NetworkDisconnect" });
//     }
//     return next(); // already on disconnect page
//   }

//   if (!isOffline && to.name === "NetworkDisconnect") {
//     const lastPage = localStorage.getItem("last_visited_page");

//     if (lastPage) {
//       localStorage.removeItem("last_visited_page");
//       return next(lastPage);
//     }

//     return next("/");
//   }

//   next();
// });
// router.beforeEach((to, from, next) => {
//   const user = JSON.parse(localStorage.getItem("user"));

//   if (to.meta.roles && !to.meta.roles.includes(user?.role)) {
//     return next({
//       name: "AccessDenied",
//       params: { resourceName: to.name },
//     });
//   }

//   next();
// });

// router.beforeEach((to, from, next) => {
//   if (to.name !== "NetworkDisconnect") {
//     localStorage.setItem("last_visited_page", to.fullPath);
//   }

//   if (!window.navigator.onLine && to.name !== "NetworkDisconnect") {
//     next({ name: "NetworkDisconnect" });
//   } else {
//     next();
//   }
// });

export default router;
